import i18n from '../i18n';

export interface ContactReferenceEntity {
  type: 'contact';
  offset: number;
  length: number;
  userId: string;
}

export interface ContactRouteReference {
  userId: string;
  expectedUsername?: string;
}

export interface TextSegment {
  text: string;
  userId?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const USERNAME = /^@[A-Za-z0-9_]{1,32}$/;
const ROUTE_USERNAME = /^[A-Za-z0-9_]{1,32}$/;

/** Проверяет только наш узкий entity-формат и UTF-16-диапазон видимого @username. */
export function parseContactReferenceEntity(text: string, entities: unknown): ContactReferenceEntity | null {
  if (!Array.isArray(entities)) return null;
  for (const raw of entities) {
    if (!raw || typeof raw !== 'object') continue;
    const entity = raw as Record<string, unknown>;
    const { type, offset, length, userId } = entity;
    if (type !== 'contact' || !Number.isInteger(offset) || !Number.isInteger(length) || typeof userId !== 'string' || !UUID.test(userId)) continue;
    const start = offset as number;
    const size = length as number;
    if (start < 0 || size <= 1 || start > text.length - size) continue;
    if (!USERNAME.test(text.slice(start, start + size))) continue;
    return { type, offset: start, length: size, userId };
  }
  return null;
}

export function splitTextByContact(text: string, entities: unknown): TextSegment[] {
  const entity = parseContactReferenceEntity(text, entities);
  if (!entity) return [{ text }];
  return [
    { text: text.slice(0, entity.offset) },
    { text: text.slice(entity.offset, entity.offset + entity.length), userId: entity.userId },
    { text: text.slice(entity.offset + entity.length) },
  ].filter((part) => part.text.length > 0);
}

export function contactUsernameMismatch(expectedUsername: string | undefined, profileUsername: string): boolean {
  return expectedUsername !== undefined && expectedUsername.toLowerCase() !== profileUsername.toLowerCase();
}

export function contactProfileError(status: number | undefined): string {
  return status === 404 ? i18n.t('misc2:contactReferenceNotFound') : i18n.t('misc2:contactReferenceProfileFailed');
}

export function contactReferenceHref(userId: string, expectedUsername?: string): string {
  if (!UUID.test(userId)) throw new Error('Invalid user id');
  if (expectedUsername !== undefined && !ROUTE_USERNAME.test(expectedUsername)) throw new Error('Invalid username');
  return `/user/${userId}${expectedUsername ? `?username=${encodeURIComponent(expectedUsername)}` : ''}`;
}

export function readContactRoute(pathname: string, search = ''): ContactRouteReference | null {
  const match = pathname.match(/^\/user\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
  if (!match) return null;
  if (!search) return { userId: match[1] };
  if (!search.startsWith('?')) return null;
  const params = new URLSearchParams(search);
  if (params.size !== 1 || !params.has('username')) return null;
  const expectedUsername = params.get('username');
  if (!expectedUsername || !ROUTE_USERNAME.test(expectedUsername)) return null;
  return { userId: match[1], expectedUsername };
}

export function parseStoredContactRoute(value: string): ContactRouteReference | null {
  const separator = value.indexOf('?');
  const pathname = separator < 0 ? value : value.slice(0, separator);
  const search = separator < 0 ? '' : value.slice(separator);
  const parsed = readContactRoute(pathname, search);
  if (parsed) return parsed;
  // Совместимость с ранней T56-записью, где сохранялся один UUID.
  return readContactRoute(`/user/${value}`);
}

export function desktopContactUri(userId: string, expectedUsername?: string): string {
  if (!UUID.test(userId)) throw new Error('Invalid user id');
  if (expectedUsername !== undefined && !ROUTE_USERNAME.test(expectedUsername)) throw new Error('Invalid username');
  return `homechat://user?id=${userId}${expectedUsername ? `&u=${expectedUsername}` : ''}`;
}

export interface ContactReferenceMessage {
  text: string;
  entities?: ContactReferenceEntityPayload[];
}

export interface ContactReferenceEntityPayload {
  type: 'contact';
  offset: number;
  length: number;
  userId: string;
}

/** Формат как в KMP (contactReferenceMessage): обычный текст + opaque entity; сервер entities не читает. */
export function buildContactReferenceMessage(contact: { id: string; username: string; displayName: string }): ContactReferenceMessage {
  const name = contact.displayName.trim() || contact.username;
  const hasUsername = /^[A-Za-z0-9_]{1,32}$/.test(contact.username);
  const text = hasUsername ? `Контакт: ${name} (@${contact.username})` : `Контакт: ${name}`;
  if (!hasUsername || !UUID.test(contact.id)) return { text };
  const mention = `@${contact.username}`;
  return { text, entities: [{ type: 'contact', offset: text.lastIndexOf(mention), length: mention.length, userId: contact.id }] };
}

/** Фильтр списка контактов по имени/логину (как filterAttachContacts в KMP). */
export function filterContacts<T extends { username: string; displayName: string }>(contacts: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  return contacts.filter((u) => !q || u.displayName.toLowerCase().includes(q) || u.username.toLowerCase().includes(q));
}
